import { CircleAlert, CircleCheck, CircleDot, Info, OctagonAlert, ShieldCheck, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { usePodeVer } from '@/hooks/use-acesso';
import type { SeveridadeIncidente, StatusIncidente } from '@/data/types';
import { ROTULO_SEVERIDADE, ROTULO_STATUS_INCIDENTE } from '@/lib/incidentes';
import { plural } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Severidade e estado de incidente — Hard rule 2: nunca só cor. Cada severidade
 * leva glifo + rótulo (crítica > alta > média > baixa em cor E em ícone).
 */

type Tom = 'neutral' | 'warning' | 'danger';

const TOM_SEVERIDADE: Record<SeveridadeIncidente, Tom> = {
  baixa: 'neutral',
  media: 'warning',
  alta: 'danger',
  critica: 'danger',
};

const ICONE_SEVERIDADE: Record<SeveridadeIncidente, typeof Info> = {
  baixa: Info,
  media: CircleAlert,
  alta: TriangleAlert,
  critica: OctagonAlert,
};

export function SeveridadeBadge({ severidade, className }: { severidade: SeveridadeIncidente; className?: string }) {
  const Icone = ICONE_SEVERIDADE[severidade];
  return (
    <Badge
      tone={TOM_SEVERIDADE[severidade]}
      filled={severidade === 'critica'}
      icon={<Icone aria-hidden="true" />}
      className={className}
    >
      {ROTULO_SEVERIDADE[severidade]}
    </Badge>
  );
}

const TOM_STATUS: Record<StatusIncidente, 'primary' | 'info' | 'success'> = {
  aberto: 'primary',
  mitigado: 'info',
  resolvido: 'success',
};

const ICONE_STATUS: Record<StatusIncidente, typeof CircleDot> = {
  aberto: CircleDot,
  mitigado: ShieldCheck,
  resolvido: CircleCheck,
};

export function StatusIncidenteBadge({ status, className }: { status: StatusIncidente; className?: string }) {
  const Icone = ICONE_STATUS[status];
  return (
    <Badge tone={TOM_STATUS[status]} icon={<Icone aria-hidden="true" />} className={className}>
      {ROTULO_STATUS_INCIDENTE[status]}
    </Badge>
  );
}

/**
 * Selo "N incidente(s)" de uma versão, na cor da MAIOR severidade não resolvida.
 * É um link para os incidentes da versão (ou botão, se vier `aoClicar`).
 */
export function IncidenteSelo({
  quantidade,
  severidade,
  versao,
  aoClicar,
  className,
}: {
  quantidade: number;
  severidade: SeveridadeIncidente | null;
  versao: string;
  aoClicar?: () => void;
  className?: string;
}) {
  const ver = usePodeVer('incidentes');
  if (quantidade <= 0 || !ver) return null;
  const nivel = severidade ?? 'media';
  const Icone = ICONE_SEVERIDADE[nivel];
  const rotulo = plural(quantidade, 'incidente', 'incidentes');
  const titulo = `${rotulo} em aberto na versão ${versao}${severidade ? ` — maior severidade: ${ROTULO_SEVERIDADE[severidade].toLowerCase()}` : ''}`;
  const conteudo = (
    <Badge
      tone={TOM_SEVERIDADE[nivel]}
      filled={nivel === 'critica'}
      icon={<Icone aria-hidden="true" />}
      className="cursor-pointer transition-[filter] duration-fast ease-standard group-hover:brightness-95"
    >
      {rotulo}
    </Badge>
  );
  const classes = cn('group inline-flex rounded-chip', className);
  if (aoClicar) {
    return (
      <button type="button" onClick={aoClicar} title={titulo} aria-label={titulo} className={classes}>
        {conteudo}
      </button>
    );
  }
  const consulta = new URLSearchParams([['versao', versao], ['status', 'aberto'], ['status', 'mitigado']]);
  return (
    <Link to={`/incidentes?${consulta.toString()}`} title={titulo} aria-label={titulo} className={classes}>
      {conteudo}
    </Link>
  );
}
